import React, { useState, useEffect, useCallback, startTransition } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import EndpointActivate from './components/EndpointActivate';
import EndpointDevices from './components/EndpointDevices';
import Home from './components/Home';
import { ThemeProvider } from './components/ThemeProvider';
import Pricing from './components/Pricing';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import WorkspaceSettings from './components/WorkspaceSettings';
import WorkspaceSelector from './components/WorkspaceSelector';
import AuditWizard from './components/AuditWizard';
import AuditDetail from './components/AuditDetail';
import ControlDetail from './components/ControlDetail';
import RiskRegister from './components/RiskRegister';
import VaptDashboard from './components/Vapt/VaptDashboard';
import DevicesPage from './components/devices/DevicesPage';
import DeviceDetail from './components/devices/DeviceDetail';
import DeviceClientDownload from './components/devices/DeviceClientDownload';
import AuditProfilePage from './components/AuditProfile/AuditProfilePage';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import SuperAdminCustomers from './components/SuperAdminCustomers';
import SuperAdminPrompts from './components/SuperAdminPrompts';
import SuperAdminTechnologies from './components/SuperAdminTechnologies';
import SuperAdminAuditProfile from './components/SuperAdminAuditProfile';
import EmployeeList from './components/EmployeeList';
import AcceptInvitePage from './components/AcceptInvitePage';
import EmployeePortal from './components/EmployeePortal';
import AddEmployee from './components/AddEmployee';
import EmployeeDetail from './components/EmployeeDetail';
import PolicyWizard from './components/PolicyWizard';
import PolicyList from './components/PolicyList';
import PolicyDetail from './components/PolicyDetail';
import TrainingWizard from './components/TrainingWizard';
import TrainingList from './components/TrainingList';
import TrainingTake from './components/TrainingTake';
import TrainingResult from './components/TrainingResult';
import TrainingDetail from './components/TrainingDetail';
import SuperAdminAllAudits from './components/SuperAdminAllAudits';
import SuperAdminAuditors from './components/SuperAdminAuditors';
import AuditorDashboard from './components/AuditorDashboard';
import TechnologyConnections from './components/TechnologyConnections';
import MyWatches from './components/MyWatches';
import GoogleWorkspaceDetail from './components/GoogleWorkspaceDetail';
import Microsoft365Detail from './components/Microsoft365Detail';
import EvidenceDetails from './components/EvidenceDetails';
import VoiceAssessment from './components/VoiceAssessment';
import VoiceAssessmentHistory from './components/VoiceAssessmentHistory';
import AssessmentReport from './components/AssessmentReport';
import SuperAdminSignups from './components/SuperAdminSignups';
import SuperAdminManagement from './components/SuperAdminManagement';
import SuperAdminMcp from './components/SuperAdminMcp';
import ServerTools from './components/ServerTools';
import EmailManagement from './components/EmailManagement';
import S3Settings from './components/S3Settings';
import EnvironmentSettings from './components/EnvironmentSettings';
import OpenAiSettings from './components/OpenAiSettings';
import PendingApproval from './components/PendingApproval';
import ResetPassword from './components/ResetPassword';
import DPA from './components/DPA';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AboutUs from './components/AboutUs';
import Contact from './components/Contact';
import Features from './components/Features';
import Trust from './components/Trust';
import Security from './components/Security';
import TrustMarketing from './components/TrustMarketing/TrustMarketingPage';
import TrustCenterAdmin from './components/TrustCenterAdmin';
import PublicTrustCenter from './components/PublicTrustCenter';
import ScrollToTop from './components/ScrollToTop';
import NotFoundPage from './components/NotFoundPage';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Using relative URLs for API calls
const API = '/api';

// Axios interceptor for auth token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — lazy session invalidation for suspended accounts.
// Backend signals suspension with `403 + body.detail.code === "account_suspended"`.
// We force a sign-out and a one-shot toast so the user is told why,
// not silently bounced. Other 403s pass through unchanged.
// (Per approved spec Q2=A, Feb 2026 — keeps the auth-cookie work for later.)
axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 403 && detail && typeof detail === 'object' && detail.code === 'account_suspended') {
      try {
        // One-shot guard so we don't toast on every queued request that
        // 403'd in the same render cycle.
        if (!window.__ETHIRA_SUSPENDED_TOASTED__) {
          window.__ETHIRA_SUSPENDED_TOASTED__ = true;
          import('sonner').then(({ toast }) =>
            toast.error(detail.message || 'Your access has been suspended.', { duration: 10000 })
          );
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('emulated_user');
        localStorage.removeItem('current_workspace');
        // Use a hard nav so any in-flight component state is wiped.
        setTimeout(() => { window.location.href = '/login?suspended=1'; }, 400);
      } catch { /* no-op */ }
    }
    return Promise.reject(error);
  },
);

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emulating, setEmulating] = useState(false);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user, workspaces, current_workspace } = response.data;
      
      localStorage.setItem('token', access_token);
      setUser(user);
      try { localStorage.setItem('user', JSON.stringify(user)); } catch { /* no-op */ }
      setWorkspaces(workspaces || []);
      setCurrentWorkspace(current_workspace);
      
      // Store workspace info for reference
      if (current_workspace) {
        localStorage.setItem('current_workspace', JSON.stringify(current_workspace));
      }
      
      return { user, workspaces, current_workspace };
    } catch (error) {
      throw error;
    }
  };

  const register = async (email, password, fullName, workspaceName) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        email, 
        password,
        full_name: fullName,
        workspace_name: workspaceName
      });
      
      // New registration flow: user is not logged in, just returns success message
      // Response: { success: true, message: "...", email: "...", approval_status: "pending" }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const selectWorkspace = async (workspaceId) => {
    try {
      const response = await axios.post(`${API}/auth/select-workspace`, { 
        workspace_id: workspaceId 
      });
      const { access_token, user, workspaces, current_workspace } = response.data;
      
      localStorage.setItem('token', access_token);
      setUser(user);
      try { localStorage.setItem('user', JSON.stringify(user)); } catch { /* no-op */ }
      setWorkspaces(workspaces || []);
      setCurrentWorkspace(current_workspace);
      
      if (current_workspace) {
        localStorage.setItem('current_workspace', JSON.stringify(current_workspace));
      }
      
      return current_workspace;
    } catch (error) {
      throw error;
    }
  };

  const switchWorkspace = async (workspaceId) => {
    try {
      const response = await axios.post(`${API}/auth/select-workspace`, { 
        workspace_id: workspaceId 
      });
      const { access_token, current_workspace } = response.data;
      
      localStorage.setItem('token', access_token);
      setCurrentWorkspace(current_workspace);
      
      if (current_workspace) {
        localStorage.setItem('current_workspace', JSON.stringify(current_workspace));
      }
      
      // Reload page to refresh all data with new workspace context
      window.location.reload();
    } catch (error) {
      throw error;
    }
  };

  const emulateCustomer = async (userId, workspaceId = null) => {
    // Store original super admin token, workspace AND identity before emulation
    const originalToken = localStorage.getItem('token');
    const originalWorkspace = localStorage.getItem('current_workspace');

    localStorage.setItem('original_admin_token', originalToken);
    if (originalWorkspace) {
      localStorage.setItem('original_admin_workspace', originalWorkspace);
    }
    try { localStorage.setItem('original_admin_user', JSON.stringify(user)); } catch { /* no-op */ }
    try { localStorage.setItem('original_admin_workspaces', JSON.stringify(workspaces)); } catch { /* no-op */ }

    const payload = { user_id: userId };
    if (workspaceId) payload.workspace_id = workspaceId;
    const response = await axios.post(`${API}/admin/emulate`, payload);
    const {
      access_token,
      user: emulatedUser,
      workspaces: emulatedWorkspaces,
      current_workspace,
    } = response.data;

    localStorage.setItem('token', access_token);
    localStorage.setItem('emulated_user', 'true');
    if (current_workspace) {
      localStorage.setItem('current_workspace', JSON.stringify(current_workspace));
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Change the location AND the emulated auth state in ONE transition so
    // React commits them atomically. Otherwise the role change (urgent) would
    // render the still-mounted super-admin-gated source route (/admin/customers)
    // with the customer role, and its PrivateRoute would redirect to "/".
    // Committing both together means that gated route is never in the tree
    // with the wrong role. No reload, no flash, no layout jump.
    const role = current_workspace?.role || 'owner';
    const path = role === 'auditor'
      ? '/auditor/dashboard'
      : role === 'employee'
        ? '/employee/portal'
        : '/dashboard';
    startTransition(() => {
      setUser(emulatedUser);
      setWorkspaces(emulatedWorkspaces || []);
      setCurrentWorkspace(current_workspace);
      setEmulating(true);
      navigate(path);
    });

    return emulatedUser;
  };

  const returnToAdmin = async () => {
    try {
      // Restore original super admin token and workspace
      const originalToken = localStorage.getItem('original_admin_token');
      const originalWorkspace = localStorage.getItem('original_admin_workspace');

      if (!originalToken) {
        logout();
        return;
      }

      localStorage.setItem('token', originalToken);
      localStorage.removeItem('original_admin_token');
      localStorage.removeItem('emulated_user');
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;

      // Restore super admin's workspace context
      let adminWorkspace = null;
      if (originalWorkspace) {
        localStorage.setItem('current_workspace', originalWorkspace);
        localStorage.removeItem('original_admin_workspace');
        try { adminWorkspace = JSON.parse(originalWorkspace); } catch { /* no-op */ }
      } else {
        adminWorkspace = { id: 'super_admin', name: 'Super Admin', role: 'super_admin' };
        localStorage.setItem('current_workspace', JSON.stringify(adminWorkspace));
      }

      // Restore the cached admin identity into React state synchronously so
      // the /admin route renders immediately — no reload, no flash.
      let adminUser = null;
      let adminWorkspaces = [];
      try { adminUser = JSON.parse(localStorage.getItem('original_admin_user')); } catch { /* no-op */ }
      try { adminWorkspaces = JSON.parse(localStorage.getItem('original_admin_workspaces') || '[]'); } catch { /* no-op */ }
      localStorage.removeItem('original_admin_user');
      localStorage.removeItem('original_admin_workspaces');

      // Commit the restored super-admin state AND the navigation atomically in
      // one transition, so the /admin route never renders with the emulated
      // (non-super-admin) role and bounces to "/". Smooth, no reload, no jump.
      if (adminUser) {
        startTransition(() => {
          setUser(adminUser);
          setCurrentWorkspace(adminWorkspace);
          setWorkspaces(adminWorkspaces || []);
          setEmulating(false);
          navigate('/admin');
        });
      } else {
        // Fallback: rebuild identity from the server if the cache was lost.
        setCurrentWorkspace(adminWorkspace);
        setWorkspaces(adminWorkspaces || []);
        setEmulating(false);
        await checkAuth();
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error returning to admin:', error);
      // Fallback: logout and redirect to login
      logout();
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('emulated_user');
    localStorage.removeItem('current_workspace');
    setUser(null);
    setWorkspaces([]);
    setCurrentWorkspace(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(prev => {
      const next = { ...prev, ...updatedUserData };
      // Keep localStorage.user in sync so a hard refresh / new tab still has it.
      try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* no-op */ }
      return next;
    });
  };

  // Wrapped in useCallback([]) so its identity is stable and it can be
  // legitimately included in the dep array of the mount effect below
  // (satisfies react-hooks/exhaustive-deps without a suppression).
  // The function reads tokens fresh from localStorage on each call, so
  // no stale-closure risk.
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        const { user } = response.data;
        
        setUser(user);
        try { localStorage.setItem('user', JSON.stringify(user)); } catch { /* no-op */ }

        // Restore workspace info from localStorage if available
        const savedWorkspace = localStorage.getItem('current_workspace');
        if (savedWorkspace) {
          try {
            const workspace = JSON.parse(savedWorkspace);
            setCurrentWorkspace(workspace);
            
            // If workspace info exists, fetch all user's workspaces for switcher
            if (workspace && workspace.id !== 'super_admin') {
              try {
                const workspacesResponse = await axios.get(`${API}/user/workspaces`);
                setWorkspaces(workspacesResponse.data || []);
              } catch (err) {
                console.error('Error fetching workspaces:', err);
                // Not critical, just means workspace switcher won't work
              }
            }
          } catch (e) {
            console.error('Error parsing saved workspace:', e);
          }
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('emulated_user');
        localStorage.removeItem('current_workspace');
        setUser(null);
        setWorkspaces([]);
        setCurrentWorkspace(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    setUser,
    workspaces,
    setWorkspaces,
    currentWorkspace,
    setCurrentWorkspace,
    login,
    register,
    selectWorkspace,
    switchWorkspace,
    emulateCustomer,
    returnToAdmin,
    logout,
    updateUser,
    loading,
    emulating,
    isEmulated: localStorage.getItem('emulated_user') === 'true'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const PrivateRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, currentWorkspace, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (requireSuperAdmin && currentWorkspace?.role !== 'super_admin') {
    return <Navigate to="/" />;
  }
  
  return children;
};

import SuperAdminVoiceAgent from './components/SuperAdminVoiceAgent';
import VoicePromptManagement from './components/VoicePromptManagement';

function App() {
  return (
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <ThemeProvider>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/security" element={<Security />} />
            <Route path="/trust" element={<TrustMarketing />} />
            <Route path="/security" element={<Security />} />
            <Route path="/trust-legacy" element={<Trust />} />
            <Route path="/t/:slug" element={<PublicTrustCenter />} />
            <Route path="/t/:slug/:section" element={<PublicTrustCenter />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/dpa" element={<DPA />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Login initialMode="register" />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/select-workspace" element={<WorkspaceSelector />} />
            
            {/* Super Admin Routes */}
            <Route path="/admin" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminDashboard />
              </PrivateRoute>
            } />
            <Route path="/admin/customers" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminCustomers />
              </PrivateRoute>
            } />
            <Route path="/admin/prompts" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminPrompts />
              </PrivateRoute>
            } />
            <Route path="/super-admin/all-audits" element={
              <PrivateRoute allowedRoles={['super_admin']}>
                <SuperAdminAllAudits />
              </PrivateRoute>
            } />
            <Route path="/super-admin/technologies" element={
              <PrivateRoute allowedRoles={['super_admin']}>
                <SuperAdminTechnologies />
              </PrivateRoute>
            } />
            <Route path="/super-admin/audit-profile" element={
              <PrivateRoute allowedRoles={['super_admin']}>
                <SuperAdminAuditProfile />
              </PrivateRoute>
            } />
            <Route path="/admin/auditors" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminAuditors />
              </PrivateRoute>
            } />
            <Route path="/admin/voice-agent" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminVoiceAgent />
              </PrivateRoute>
            } />
            <Route path="/super-admin/voice-prompts" element={
              <PrivateRoute requireSuperAdmin={true}>
                <VoicePromptManagement />
              </PrivateRoute>
            } />
            <Route path="/admin/signups" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminSignups />
              </PrivateRoute>
            } />
            <Route path="/admin/super-admin-management" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminManagement />
              </PrivateRoute>
            } />
            <Route path="/admin/email-management" element={
              <PrivateRoute requireSuperAdmin={true}>
                <EmailManagement />
              </PrivateRoute>
            } />
            <Route path="/admin/r2-settings" element={
              <PrivateRoute requireSuperAdmin={true}>
                <S3Settings />
              </PrivateRoute>
            } />
            <Route path="/admin/environment-settings" element={
              <PrivateRoute requireSuperAdmin={true}>
                <EnvironmentSettings />
              </PrivateRoute>
            } />
            <Route path="/admin/openai-settings" element={
              <PrivateRoute requireSuperAdmin={true}>
                <OpenAiSettings />
              </PrivateRoute>
            } />
            <Route path="/admin/server-tools" element={
              <PrivateRoute requireSuperAdmin={true}>
                <ServerTools />
              </PrivateRoute>
            } />
            <Route path="/admin/mcp" element={
              <PrivateRoute requireSuperAdmin={true}>
                <SuperAdminMcp />
              </PrivateRoute>
            } />
            
            {/* Auditor Routes */}
            <Route path="/auditor/dashboard" element={
              <PrivateRoute>
                <AuditorDashboard />
              </PrivateRoute>
            } />
            
            {/* Regular User Routes */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            <Route path="/workspace/settings" element={
              <PrivateRoute>
                <WorkspaceSettings />
              </PrivateRoute>
            } />
            <Route path="/audit/new" element={
              <PrivateRoute>
                <AuditWizard />
              </PrivateRoute>
            } />
            <Route path="/risks" element={
              <PrivateRoute>
                <RiskRegister />
              </PrivateRoute>
            } />
            <Route path="/vapt" element={
              <PrivateRoute>
                <VaptDashboard />
              </PrivateRoute>
            } />
            <Route path="/devices" element={
              <PrivateRoute>
                <DevicesPage />
              </PrivateRoute>
            } />
            <Route path="/devices/download" element={
              <PrivateRoute>
                <DeviceClientDownload />
              </PrivateRoute>
            } />
            <Route path="/devices/:deviceId" element={
              <PrivateRoute>
                <DeviceDetail />
              </PrivateRoute>
            } />
            <Route path="/trust-center" element={
              <PrivateRoute>
                <TrustCenterAdmin />
              </PrivateRoute>
            } />
            <Route path="/audit-profile" element={
              <PrivateRoute>
                <AuditProfilePage />
              </PrivateRoute>
            } />
            <Route path="/audit/:auditId" element={
              <PrivateRoute>
                <AuditDetail />
              </PrivateRoute>
            } />
            <Route path="/control/:controlId" element={
              <PrivateRoute>
                <ControlDetail />
              </PrivateRoute>
            } />
            
            {/* Employee Module Routes */}
            <Route path="/employees" element={
              <PrivateRoute>
                <EmployeeList />
              </PrivateRoute>
            } />
            <Route path="/endpoint/activate" element={
              <PrivateRoute>
                <EndpointActivate />
              </PrivateRoute>
            } />
            <Route path="/endpoint/devices" element={
              <PrivateRoute>
                <EndpointDevices />
              </PrivateRoute>
            } />
            <Route path="/employees/new" element={
              <PrivateRoute>
                <AddEmployee />
              </PrivateRoute>
            } />
            <Route path="/employees/:employeeId" element={
              <PrivateRoute>
                <EmployeeDetail />
              </PrivateRoute>
            } />
            <Route path="/employee/portal" element={
              <PrivateRoute>
                <EmployeePortal />
              </PrivateRoute>
            } />

            {/* Technology Connections Route */}
            <Route path="/technologies" element={
              <PrivateRoute>
                <TechnologyConnections />
              </PrivateRoute>
            } />

            {/* Per-technology detail page — Google Workspace */}
            <Route path="/technologies/google-workspace" element={
              <PrivateRoute>
                <GoogleWorkspaceDetail />
              </PrivateRoute>
            } />

            <Route path="/technologies/microsoft-365" element={
              <PrivateRoute>
                <Microsoft365Detail />
              </PrivateRoute>
            } />

            {/* Evidence Details Route */}
            <Route path="/technologies/:technology/evidence" element={
              <PrivateRoute>
                <EvidenceDetails />
              </PrivateRoute>
            } />

            {/* My Watches — Phase 2B subscription management + in-app feed */}
            <Route path="/watches" element={
              <PrivateRoute>
                <MyWatches />
              </PrivateRoute>
            } />

            {/* Policy Module Routes */}
            <Route path="/policies" element={
              <PrivateRoute>
                <PolicyList />
              </PrivateRoute>
            } />
            <Route path="/policies/wizard" element={
              <PrivateRoute>
                <PolicyWizard />
              </PrivateRoute>
            } />
            <Route path="/policies/:policyId" element={
              <PrivateRoute>
                <PolicyDetail />
              </PrivateRoute>
            } />

            {/* Training Module Routes */}
            <Route path="/trainings" element={
              <PrivateRoute>
                <TrainingList />
              </PrivateRoute>
            } />
            <Route path="/trainings/wizard" element={
              <PrivateRoute>
                <TrainingWizard />
              </PrivateRoute>
            } />
            <Route path="/trainings/:trainingId" element={
              <PrivateRoute>
                <TrainingDetail />
              </PrivateRoute>
            } />
            <Route path="/trainings/:trainingId/take" element={
              <PrivateRoute>
                <TrainingTake />
              </PrivateRoute>
            } />
            <Route path="/trainings/:trainingId/result" element={
              <PrivateRoute>
                <TrainingResult />
              </PrivateRoute>
            } />

            {/* AI Voice Assessment Routes */}
            <Route path="/assessment/voice" element={
              <PrivateRoute>
                <VoiceAssessment />
              </PrivateRoute>
            } />
            <Route path="/assessment/voice/history" element={
              <PrivateRoute>
                <VoiceAssessmentHistory />
              </PrivateRoute>
            } />
            <Route path="/assessment/report/:sessionId" element={
              <PrivateRoute>
                <AssessmentReport />
              </PrivateRoute>
            } />

            {/* Catch-all — keeps the production blank-page failure mode
                from ever happening again. If `/accept-invite` lands here
                (e.g. a stale production bundle without that route) the
                NotFoundPage auto-redirects to /login?mode=otp so the
                user can still finish onboarding with the 6-digit code
                from the same email. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ThemeProvider>
          <Toaster />
          </AuthProvider>
        </BrowserRouter>
      </div>
  );
}

export default App;